/**
 * fd.motion — GPU-composited animation helpers (Web Animations API).
 *
 * Drop-in replacements for jQuery fadeIn/fadeOut/slideDown/slideUp.
 * Each function accepts a raw DOM element (or jQuery object) and returns
 * a Promise that resolves when the animation finishes.
 *
 * Con "reducir movimiento" (prefers-reduced-motion: reduce) nada se anima: los
 * fundidos y deslizamientos duran 0 ms (mismo estado final, la promesa y el
 * callback llegan igual) y el desplazamiento es instantáneo. El pulso de
 * atención (fdPulse) solo cambia la sombra y se queda.
 */

var EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

export function movimientoReducido() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function duracion(pedida, porDefecto) {
  return movimientoReducido() ? 0 : (pedida || porDefecto);
}

function toEl(elOrJq) {
  if (!elOrJq) return null;
  return elOrJq.jquery ? elOrJq[0] : (elOrJq.nodeType ? elOrJq : document.querySelector(elOrJq));
}

// ---------------------------------------------------------------------------
// Fade
// ---------------------------------------------------------------------------

export function fdFadeIn(el, duration) {
  el = toEl(el);
  if (!el) return Promise.resolve();
  duration = duracion(duration, 150);
  el.style.display = '';
  var anim = el.animate(
    [{ opacity: 0 }, { opacity: 1 }],
    { duration: duration, easing: EASE, fill: 'forwards' }
  );
  return anim.finished.then(function () {
    el.style.opacity = '';
    anim.cancel();
  });
}

export function fdFadeOut(el, duration, cb) {
  el = toEl(el);
  if (!el) return Promise.resolve();
  duration = duracion(duration, 150);
  var anim = el.animate(
    [{ opacity: 1 }, { opacity: 0 }],
    { duration: duration, easing: EASE, fill: 'forwards' }
  );
  return anim.finished.then(function () {
    el.style.display = 'none';
    el.style.opacity = '';
    anim.cancel();
    if (typeof cb === 'function') cb.call(el);
  });
}

// ---------------------------------------------------------------------------
// Slide (uses transform + opacity for GPU compositing)
// ---------------------------------------------------------------------------

export function fdSlideDown(el, duration) {
  el = toEl(el);
  if (!el) return Promise.resolve();
  duration = duracion(duration, 200);

  el.style.display = '';
  el.style.overflow = 'hidden';
  var h = el.scrollHeight;
  var anim = el.animate(
    [
      { opacity: 0, maxHeight: '0px', paddingTop: '0px', paddingBottom: '0px' },
      { opacity: 1, maxHeight: h + 'px', paddingTop: '', paddingBottom: '' }
    ],
    { duration: duration, easing: EASE, fill: 'forwards' }
  );
  return anim.finished.then(function () {
    el.style.overflow = '';
    el.style.maxHeight = '';
    el.style.opacity = '';
    anim.cancel();
  });
}

export function fdSlideUp(el, duration, cb) {
  el = toEl(el);
  if (!el) return Promise.resolve();
  duration = duracion(duration, 200);

  var h = el.scrollHeight;
  el.style.overflow = 'hidden';
  var anim = el.animate(
    [
      { opacity: 1, maxHeight: h + 'px' },
      { opacity: 0, maxHeight: '0px', paddingTop: '0px', paddingBottom: '0px' }
    ],
    { duration: duration, easing: EASE, fill: 'forwards' }
  );
  return anim.finished.then(function () {
    el.style.display = 'none';
    el.style.overflow = '';
    el.style.maxHeight = '';
    el.style.opacity = '';
    anim.cancel();
    if (typeof cb === 'function') cb.call(el);
  });
}

// ---------------------------------------------------------------------------
// Pulse — attention-grabbing highlight (validation errors, etc.)
// ---------------------------------------------------------------------------

export function fdPulse(el, duration) {
  el = toEl(el);
  if (!el) return Promise.resolve();
  duration = duration || 600;
  var color = getComputedStyle(document.documentElement).getPropertyValue('--bs-primary').trim() || '#4361ee';
  var anim = el.animate(
    [
      { boxShadow: '0 0 0 0 ' + color + '66', offset: 0 },
      { boxShadow: '0 0 0 6px ' + color + '00', offset: 0.7 },
      { boxShadow: '0 0 0 0 ' + color + '00', offset: 1 }
    ],
    { duration: duration, easing: 'ease', iterations: 2 }
  );
  return anim.finished;
}

// ---------------------------------------------------------------------------
// Scroll — smooth scroll to element or position
// ---------------------------------------------------------------------------

export function fdScrollTo(target, offset) {
  var el = typeof target === 'number' ? null : toEl(target);
  if (el) {
    var top = el.getBoundingClientRect().top + window.scrollY - (offset || 0);
    window.scrollTo({ top: top, behavior: movimientoReducido() ? 'auto' : 'smooth' });
  } else if (typeof target === 'number') {
    window.scrollTo({ top: target, behavior: movimientoReducido() ? 'auto' : 'smooth' });
  }
}
